import React, { useEffect, useState } from 'react';
import { Form, Input, InputNumber, Button, Space, Card, Select, message, Spin, Row, Col, Typography, Tag, Divider, Alert, Modal } from 'antd';
import { useHistory, useParams } from 'react-router-dom';
import { useConfig } from '../../contexts/ConfigContext';
import { useCompany } from '../../contexts/CompanyContext';
import useAuth from '../../hooks/useAuth';
import { useOrder, useTemplates } from '../../hooks/useManufacturing';
import { useValidateResourceCompany } from '../../hooks/useValidateResourceCompany';
import { useItems, useStoreFetch } from '../../hooks/useInventory';
import { useSecureRequest } from '../../hooks/useSecureRequest';
import { useUser } from '../../contexts/UserContext';
import { createId } from '../../utils';


const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;
const { useWatch } = Form;

const OrderForm: React.FC = () => {
  const [form] = Form.useForm();
  const history = useHistory();
  const { uid } = useParams<{ uid?: string }>();
  const { config } = useConfig();
  const { company } = useCompany();
  const { validateResourceCompany, isSwitchingCompany } = useValidateResourceCompany();
  const {user} = useUser()
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const [loading, setLoading] = useState(false);

  // States for handling template selection dynamically
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [expectedQuantity, setExpectedQuantity] = useState<number>(1);

  const { data: orderData, isLoading: isOrderLoading, mutate: mutateOrder, error: orderError } = useOrder(tokenData, { order_uid: uid || undefined, company_uid: company.current?.uid });
  const { data: templatesData, isLoading: isTemplatesLoading } = useTemplates(tokenData, { company: company.current?.id });
  const { data: inventoryData, isLoading: isInventoryLoading } = useItems(tokenData, { company: company.current?.uid, module: config.module });
  const { data: storehouseData, isLoading: isStorehouseLoading } = useStoreFetch(tokenData, { company: company.current?.uid, all: true });
  const { securePost } = useSecureRequest();

  const fetchedElements = React.useRef<Record<number, any>>({});

  const handleElementSelect = async (elementId: number) => {
    if (!elementId || (fetchedElements.current[elementId] && !fetchedElements.current[elementId]._loading)) return;
    
    // Check if we have the inventory item loaded to find its uid
    const inventoryItem = inventoryData?.itemList?.find((i: any) => i.id === elementId);
    if (!inventoryItem?.uid) return;

    fetchedElements.current[elementId] = { _loading: true }; 
    const hide = message.loading(`Obteniendo detalles de ${inventoryItem.name}...`, 0);
    try {
      const res = await securePost('/inventory/item/get-item', {
        company: company.current?.uid,
        uidItem: inventoryItem.uid
      });
      if (res?.inventoryItem) {
        fetchedElements.current[elementId] = res.inventoryItem;
        hide();
      } else {
        delete fetchedElements.current[elementId];
        hide();
        message.warning(`No se pudo obtener el detalle completo de ${inventoryItem.name}`);
      }
    } catch (e: any) {
      delete fetchedElements.current[elementId];
      hide();
      message.error(`Error al consultar ${inventoryItem.name}: ${e.message || 'Fallo de conexión'}`);
    }
  };

  const [isSyncModalVisible, setIsSyncModalVisible] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [storehouseFrom, setStorehouseFrom] = useState<number | null>(null);
  const [storehouseTo, setStorehouseTo] = useState<number | null>(null);

  const isEditMode = !!uid;
  const templates = templatesData?.templates || [];
  const inventoryItems = inventoryData?.itemList || [];
  
  const mfgType = useWatch('manufacturing_type', form) || 'DISCRETE';
  
  const getElementName = (elementId: number) => {
    return inventoryItems.find((item: any) => item.id === elementId)?.name || `Unknown (${elementId})`;
  };

  const elementOptions = inventoryItems.map((item: any) => (
    <Option key={item.id} value={item.id}>{item.name}</Option>
  ));

  useEffect(() => {
    if (orderError) {
      message.error(orderError.message || "Error al cargar la orden");
      history.push('/orders');
    }
    if (orderData?.message) {
      message.error(orderData.message);
      history.push('/orders');
    }
  }, [orderError, orderData, history]);

  // Validación de empresa: la orden debe pertenecer a la empresa activa
  useEffect(() => {
    if (isEditMode && orderData?.order) {
      validateResourceCompany(
        orderData.order.company_id,
        orderData.order.company_uid || orderData.order.companies?.uid,
        '/orders',
        'orden de producción'
      );
    }
  }, [isEditMode, orderData?.order, validateResourceCompany]);

  useEffect(() => {
    if (isEditMode && orderData?.order) {
      const order = orderData.order;
      setSelectedTemplateId(order.template_id);
      setExpectedQuantity(order.expected_quantity);
      form.setFieldsValue({
        manufacturing_type: order.manufacturing_type || 'DISCRETE',
        template_id: order.template_id,
        expected_quantity: order.expected_quantity,
        status: order.status,
        actual_quantity: order.actual_quantity,
        notes: order.notes,
        inputs: (order.inputs || order.production_order_inputs || []).map((item: any) => ({
          id: item.id,
          element_id: item.element_id,
          expected_quantity: item.expected_quantity,
          actual_quantity: item.actual_quantity || item.expected_quantity
        })),
        outputs: order.production_order_outputs?.map((out: any) => ({
          id: out.id,
          element_id: out.element_id,
          expected_quantity: out.expected_quantity,
          actual_quantity: out.actual_quantity || out.expected_quantity
        })) || []
      });
      
      // Pre-fetch elements for existing order or use Firestore data
      const processItem = (item: any) => {
        if (item.element_data) {
          fetchedElements.current[item.element_id] = item.element_data;
        } else {
          handleElementSelect(item.element_id);
        }
      };

      if (order.inputs && order.inputs.length > 0) {
        order.inputs.forEach(processItem);
      } else {
        (order.production_order_inputs || []).forEach(processItem);
      }

      if (order.outputs && order.outputs.length > 0) {
        order.outputs.forEach(processItem);
      } else {
        (order.production_order_outputs || []).forEach(processItem);
      }
    }
  }, [orderData, form, isEditMode, inventoryData]);

  const onTemplateChange = (templateId: number) => {
    setSelectedTemplateId(templateId);
    calculateExpectedItems(templateId, expectedQuantity);
  };

  const onExpectedQuantityChange = (qty: number | null) => {
    if (qty !== null && qty > 0) {
      setExpectedQuantity(qty);
      if (selectedTemplateId) {
        calculateExpectedItems(selectedTemplateId, qty);
      }
    }
  };

  const calculateExpectedItems = async (templateId: number, qty: number) => {
    let template = templates.find((t: any) => t.id === templateId);

    // Fetch full template natively to avoid N+1 queries on nested elements
    try {
      setLoading(true);
      const res = await securePost('/manufacturing/get-template', { company_uid: company.current?.uid, id: templateId });
      if (res?.template) {
         template = res.template;
      }
    } catch(error) {
       console.error("Error pre-fetching template details: ", error);
    } finally {
       setLoading(false);
    }

    if (template) {
      const multiplier = mfgType === 'PROCESS' ? 1 : (qty / (template.target_quantity || 1));
      
      if (template.inputs && template.inputs.length > 0) {
        template.inputs.forEach((i: any) => {
          if (i.element_data) {
            fetchedElements.current[i.element_id] = i.element_data;
          } else {
            handleElementSelect(i.element_id);
          }
        });
        const newInputs = template.inputs.map((item: any) => ({
          element_id: item.element_id,
          expected_quantity: item.quantity * multiplier,
        }));
        form.setFieldsValue({ inputs: newInputs });
      } else if (template.production_template_inputs) {
        const newInputs = template.production_template_inputs.map((item: any) => {
          if (item.element_data) {
            fetchedElements.current[item.element_id] = item.element_data;
          } else {
            handleElementSelect(item.element_id);
          }
          return {
            element_id: item.element_id,
            expected_quantity: item.quantity * multiplier,
          }
        });
        form.setFieldsValue({ inputs: newInputs });
      }

      if (template.outputs && template.outputs.length > 0) {
        template.outputs.forEach((o: any) => {
          if (o.element_data) {
            fetchedElements.current[o.element_id] = o.element_data;
          } else {
            handleElementSelect(o.element_id);
          }
        });
        const newOutputs = template.outputs.map((out: any) => ({
          element_id: out.element_id,
          expected_quantity: out.expected_quantity * multiplier,
        }));
        form.setFieldsValue({ outputs: newOutputs });
      } else if (template.production_template_outputs) {
        const newOutputs = template.production_template_outputs.map((out: any) => {
          if (out.element_data) {
            fetchedElements.current[out.element_id] = out.element_data;
          } else {
            handleElementSelect(out.element_id);
          }
          return {
            element_id: out.element_id,
            expected_quantity: out.expected_quantity * multiplier,
          }
        });
        form.setFieldsValue({ outputs: newOutputs });
      }
    }
  };

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const fetchFullElementData = async (list: any[]) => {
        if (!list) return [];
        return await Promise.all(list.map(async (item: any) => {
          if (!item?.element_id) return item;
          
          if (fetchedElements.current[item.element_id] && !fetchedElements.current[item.element_id]._loading) {
            return { ...item, element_data: fetchedElements.current[item.element_id] };
          }

          const inventoryItem = inventoryItems.find((i: any) => i.id === item.element_id);
          if (inventoryItem?.uid) {
            const res = await securePost('/inventory/item/get-item', {
              company: company.current?.uid,
              uidItem: inventoryItem.uid
            });
            if (res?.inventoryItem) {
              fetchedElements.current[item.element_id] = res.inventoryItem;
              return { ...item, element_data: res.inventoryItem };
            }
          }
          return item;
        }));
      };

      const populatedInputs = await fetchFullElementData(values.inputs);
      const populatedOutputs = await fetchFullElementData(values.outputs);

      const payload: any = {
        company: company.current?.uid,
        order: {
          company_id: company.current?.id,
          ...values,
          inputs: populatedInputs,
          outputs: populatedOutputs
        }
      };

      if (isEditMode) {
        payload.order.id = currentOrder?.id;
        const res = await securePost('/manufacturing/update-order', {
          ...payload,
          csrfToken: tokenData?.token?.uid
        });
        if(res && !res.error) {
          message.success('Orden de producción actualizada');
          history.push('/orders');
        } else {
          message.error(res?.message || res?.error || 'Error al actualizar la orden');
        }
      } else {
        payload.order.created_by_user_id = user?.id;
        payload.order.created_by_user_name = user?.name;
        payload.order.created_by_user_document = user?.document_number;
        payload.order.expected_quantity = expectedQuantity;
        payload.order.document_number = `ORPR${createId(8)}`;
        const res = await securePost('/manufacturing/create-order', {
          ...payload,
          csrfToken: tokenData?.token?.uid
        });
        if(res && !res.error) {
          message.success('Orden de producción creada');
          history.push('/orders');
        } else {
          message.error(res?.message || res?.error || 'Error al crear la orden');
        }
      }
    } catch (error: any) {
      message.error(error.message || 'Error al procesar la orden');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncInventory = async () => {
    if (!storehouseFrom || !storehouseTo) {
      message.error("Debes seleccionar ambas bodegas");
      return;
    }
    setSyncing(true);
    try {
      const { production_order_inputs, production_order_outputs } = currentOrder;
      const consumedItems = production_order_inputs?.reduce((acc: any[], i: any) => {
        const fullData = i.element_data || fetchedElements.current[i.element_id] || {};
        const regType = fullData.register_type;
        if (regType === "PRODUCT") {
          acc.push({
            ...fullData,
            key: fullData.key || createId(6),
            uid: fullData.uid || i.element_id,
            id: fullData.id || i.element_id,
            quantity: i.actual_quantity || i.expected_quantity,
            total_amount: 0,
            register_type: regType,
            register: acc.length + 1
          });
        }
        return acc;
      }, []) || [];
      const generatedItems = production_order_outputs?.reduce((acc: any[], o: any) => {
        const fullData = o.element_data || fetchedElements.current[o.element_id] || {};
        const regType = fullData.register_type;
        if (regType === "PRODUCT") {
          acc.push({
            ...fullData,
            key: fullData.key || createId(6),
            uid: fullData.uid || o.element_id,
            id: fullData.id || o.element_id,
            quantity: o.actual_quantity || o.expected_quantity,
            total_amount: 0,
            register_type: regType,
            register: acc.length + 1
          });
        }
        return acc;
       }, []) || [];

      // HELPER: To process a movement reporting
      const processMovement = async (docType: string, typeMod: string, itemList: any[], sFrom: number | null, sTo: number | null) => {
         const draftMovement = {
            company_id: company.current?.id,
            created_by_user_id: user?.id,
            created_by_user_name: user?.name,
            created_by_user_document: user?.document_number,
            document_number: `${docType === 'PRODUCTION_EXIT' ? 'PRSL' : 'PREN'}${createId(8)}`,
            document_type: docType,
            status: 'PENDING',
            invoice_type: 'SIMPLE_RECEIPT',
            item_list: itemList,
            items: itemList.length,
            total_amount: 0,
            total_discount_amount: 0,
            total_amount_net: 0,
            total_amount_full: 0,
            tax_amount: 0,
            storehouse_from_id: sFrom,
            storehouse_to_id: sTo,
            typeMovement: typeMod
         };
         
         const createRes = await securePost('/inventory/movement/create-movement', {
            movement: draftMovement,
            company: company.current?.uid,
            module: config.module
         });
         
         if (createRes?.message) throw new Error(createRes.message);
         if (!createRes?.movementUid) throw new Error("No se obtuvo el UID del borrador");
         
         return createRes.movementUid;
      };

      // Create EXIT for consumed items
      let exitId = null;
      let entryId = null;
      if (consumedItems.length > 0) {
        exitId = await processMovement('PRODUCTION_EXIT', 'REGISTER_EXIT', consumedItems, storehouseFrom, null);
      }

      // Create ENTRY for generated items
      if (generatedItems.length > 0) {
        entryId = await processMovement('PRODUCTION_ENTRY', 'REGISTER_ENTRY', generatedItems, null, storehouseTo);
      }

      // Mark order as synced
      await securePost('/manufacturing/update-order', {
        company: company.current?.uid,
        order: {
          id: currentOrder.id,
          inventory_synced: true,
          inventory_sync_reference: `EXIT:${exitId} ENTRY:${entryId}`
        },
        csrfToken: tokenData?.token?.uid
      });

      message.success('Inventario actualizado exitosamente');
      setIsSyncModalVisible(false);
      mutateOrder(); // reload order data
    } catch (error: any) {
      message.error(error.message || 'Error al conectar con inventario');
    } finally {
      setSyncing(false);
    }
  };

  const hasCompanyMismatch = Boolean(
    isEditMode &&
    orderData?.order?.company_id &&
    company.current?.id &&
    company.current.id !== 0 &&
    orderData.order.company_id !== company.current.id
  );

  if ((isEditMode && isOrderLoading) || isTemplatesLoading || isInventoryLoading || isStorehouseLoading || isSwitchingCompany || hasCompanyMismatch) {
    return <div style={{ textAlign: 'center', marginTop: '50px' }}><Spin size="large" tip={isSwitchingCompany || hasCompanyMismatch ? "Validando empresa de la orden..." : undefined} /></div>;
  }

  const currentOrder = orderData?.order;
  const isCompleted = currentOrder?.status === 'COMPLETED';
  const isCancelled = currentOrder?.status === 'CANCELLED';
  const disableForm = isCompleted || isCancelled;

  return (
    <Card title={isEditMode ? `Orden de Producción ${currentOrder?.document_number}` : "Nueva Orden de Producción"} style={{ margin: '24px' }}>
      {isEditMode && currentOrder && (
         <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between' }}>
           <Space>
             <Text strong>Estado Actual:</Text>
             <Tag color={currentOrder.status === 'COMPLETED' ? 'green' : 'blue'}>{currentOrder.status}</Tag>
             {currentOrder.inventory_synced && <Tag color="purple">Inventario Sincronizado</Tag>}
           </Space>
           {isCompleted && !currentOrder.inventory_synced && (
             <Button type="primary" onClick={() => setIsSyncModalVisible(true)}>
               Reportar en Almacén
             </Button>
           )}
         </div>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ expected_quantity: 1, inputs: [], outputs: [], manufacturing_type: 'DISCRETE' }}
      >
        <Form.Item name="manufacturing_type" label="Tipo de Manufactura" rules={[{ required: true }]}>
          <Select disabled={isEditMode}>
            <Option value="DISCRETE">Discreta (1 Producto Final)</Option>
            <Option value="PROCESS">Por Procesos (Múltiples Salidas/Mermas)</Option>
          </Select>
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="template_id" label="Plantilla (Receta)" rules={[{ required: mfgType === 'DISCRETE', message: 'Requerido en Discreta' }]}>
              <Select 
                placeholder="Selecciona la plantilla" 
                showSearch 
                optionFilterProp="children" 
                onChange={onTemplateChange}
                disabled={isEditMode}
                allowClear={mfgType === 'PROCESS'}
              >
                {templates.filter((t:any) => {
                  const isActiveOrCurrent = t.is_active || t.id === currentOrder?.template_id;
                  const tMfgType = t.manufacturing_type || 'DISCRETE';
                  return isActiveOrCurrent && (tMfgType === mfgType);
                }).map((t: any) => (
                  <Option key={t.id} value={t.id}>{t.name}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          {mfgType !== 'PROCESS' && (
            <Col span={12}>
              <Form.Item name="expected_quantity" label="Cantidad a Producir" rules={[{ required: true }]}>
                <InputNumber 
                  min={1} 
                  style={{ width: '100%' }} 
                  onChange={onExpectedQuantityChange}
                  disabled={isEditMode && currentOrder?.status !== 'DRAFT'}
                />
              </Form.Item>
            </Col>
          )}
        </Row>

        {isEditMode && (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="status" label="Cambiar Estado" rules={[{ required: true }]}>
                <Select disabled={disableForm}>
                  <Option value="DRAFT">Borrador</Option>
                  <Option value="IN_PROGRESS">En Progreso</Option>
                  <Option value="COMPLETED">Completado</Option>
                  <Option value="CANCELLED">Cancelado</Option>
                </Select>
              </Form.Item>
            </Col>
            
            <Form.Item noStyle dependencies={['status', 'manufacturing_type']}>
              {() => {
                const status = form.getFieldValue('status');
                const type = form.getFieldValue('manufacturing_type');
                if (status === 'COMPLETED' && type === 'DISCRETE') {
                  return (
                    <Col span={12}>
                      <Form.Item name="actual_quantity" label="Cantidad Real Producida" rules={[{ required: true }]}>
                        <InputNumber min={0.01} step={0.01} style={{ width: '100%' }} disabled={disableForm} />
                      </Form.Item>
                    </Col>
                  );
                }
                return null;
              }}
            </Form.Item>
          </Row>
        )}

        <Form.Item name="notes" label="Notas Adicionales">
          <TextArea rows={3} disabled={disableForm} />
        </Form.Item>

        <Divider orientation="left">Insumos Requeridos</Divider>
        
        {selectedTemplateId && !isEditMode && (mfgType === 'DISCRETE') && (
          <Alert message="Cantidades calculadas automáticamente según la plantilla." type="info" showIcon style={{ marginBottom: '16px' }} />
        )}

        {isEditMode && currentOrder?.status === 'COMPLETED' && (
           <Alert message="Anota las mermas o diferencias en las cantidades reales consumidas." type="warning" showIcon style={{ marginBottom: '16px' }} />
        )}

        <Form.List name="inputs">
          {(fields, { add, remove }) => (
            <>
              {fields.length > 0 && (
                <Row gutter={16} style={{ marginBottom: '8px', fontWeight: 'bold' }}>
                  <Col span={10}>Insumo</Col>
                  <Col span={7}>Cant. Teórica</Col>
                  {isEditMode && <Col span={7}>Cant. Real Usada</Col>}
                </Row>
              )}
              {fields.map(({ key, name, ...restField }) => (
                <Row key={key} gutter={16} style={{ marginBottom: 8 }} align="middle">
                  <Form.Item {...restField} name={[name, 'id']} hidden><Input /></Form.Item>
                  <Col span={10}>
                    {mfgType === 'PROCESS' && !isEditMode ? (
                      <Form.Item
                        {...restField}
                        name={[name, 'element_id']}
                        rules={[{ required: true, message: 'Insumo faltante' }]}
                      >
                        <Select placeholder="Seleccionar insumo" showSearch optionFilterProp="children" disabled={isEditMode} onChange={handleElementSelect}>
                          {elementOptions}
                        </Select>
                      </Form.Item>
                    ) : (
                      <Form.Item shouldUpdate>
                        {() => {
                          const elementId = form.getFieldValue(['inputs', name, 'element_id']);
                          return (
                            <>
                              <Form.Item {...restField} name={[name, 'element_id']} hidden><Input /></Form.Item>
                              <Input value={getElementName(elementId)} disabled />
                            </>
                          );
                        }}
                      </Form.Item>
                    )}
                  </Col>
                  <Col span={7}>
                    <Form.Item
                      {...restField}
                      name={[name, 'expected_quantity']}
                      rules={[{ required: true, message: 'Req' }]}
                    >
                      <InputNumber disabled={isEditMode || mfgType === 'DISCRETE'} min={0.01} step={0.01} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  {isEditMode && (
                    <Col span={7}>
                      <Form.Item
                        {...restField}
                        name={[name, 'actual_quantity']}
                        rules={[{ required: true, message: 'Ingrese real' }]}
                      >
                         <InputNumber min={0} step={0.01} style={{ width: '100%' }} disabled={disableForm} />
                      </Form.Item>
                    </Col>
                  )}
                  {mfgType === 'PROCESS' && !isEditMode && (
                    <Button type="text" danger onClick={() => remove(name)}>X</Button>
                  )}
                </Row>
              ))}
              {mfgType === 'PROCESS' && !isEditMode && (
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block>
                    Añadir Insumo
                  </Button>
                </Form.Item>
              )}
            </>
          )}
        </Form.List>

        <>
          <Divider orientation="left">{mfgType === 'DISCRETE' ? 'Producto Resultante Esperado' : 'Productos Resultantes (Outputs)'}</Divider>
          <Form.List name="outputs">
            {(fields, { add, remove }) => (
              <>
                {fields.length > 0 && (
                  <Row gutter={16} style={{ marginBottom: '8px', fontWeight: 'bold' }}>
                    <Col span={10}>Producto</Col>
                    <Col span={7}>Cant. Teórica</Col>
                    {isEditMode && <Col span={7}>Cant. Real Generada</Col>}
                  </Row>
                )}
                {fields.map(({ key, name, ...restField }) => (
                  <Row key={key} gutter={16} style={{ marginBottom: 8 }} align="middle">
                    <Form.Item {...restField} name={[name, 'id']} hidden><Input /></Form.Item>
                    <Col span={10}>
                      {mfgType === 'PROCESS' && !isEditMode ? (
                        <Form.Item
                          {...restField}
                          name={[name, 'element_id']}
                          rules={[{ required: true, message: 'Producto faltante' }]}
                        >
                            <Select placeholder="Seleccionar producto" showSearch optionFilterProp="children" disabled={isEditMode} onChange={handleElementSelect}>
                              {elementOptions}
                            </Select>
                          </Form.Item>
                        ) : (
                           <Form.Item shouldUpdate>
                             {() => {
                               const elementId = form.getFieldValue(['outputs', name, 'element_id']);
                               return (
                                <>
                                  <Form.Item {...restField} name={[name, 'element_id']} hidden><Input /></Form.Item>
                                  <Input value={getElementName(elementId)} disabled />
                                </>
                               );
                             }}
                           </Form.Item>
                        )}
                      </Col>
                      <Col span={7}>
                        <Form.Item
                          {...restField}
                          name={[name, 'expected_quantity']}
                          rules={[{ required: true, message: 'Req' }]}
                        >
                          <InputNumber disabled={isEditMode || mfgType === 'DISCRETE'} min={0.01} step={0.01} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      {isEditMode && (
                        <Col span={7}>
                          <Form.Item
                            {...restField}
                            name={[name, 'actual_quantity']}
                            rules={[{ required: currentOrder?.status === 'COMPLETED', message: 'Ingrese real' }]}
                          >
                             <InputNumber min={0} step={0.01} style={{ width: '100%' }} disabled={disableForm} />
                          </Form.Item>
                        </Col>
                      )}
                      {mfgType === 'PROCESS' && !isEditMode && (
                        <Button type="text" danger onClick={() => remove(name)}>X</Button>
                      )}
                    </Row>
                  ))}
                  {mfgType === 'PROCESS' && !isEditMode && (
                    <Form.Item>
                      <Button type="dashed" onClick={() => add()} block>
                        Añadir Producto Esperado
                      </Button>
                    </Form.Item>
                  )}
                </>
              )}
            </Form.List>
          </>

        {!disableForm && (
          <div style={{ position: 'sticky', bottom: 0, background: '#fff', padding: '16px', textAlign: 'right', boxShadow: '0 -2px 10px rgba(0,0,0,0.1)', zIndex: 10, margin: '24px -24px -24px -24px', borderRadius: '0 0 8px 8px' }}>
            <Space>
              <Button onClick={() => history.push('/orders')}>Cancelar</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Guardar Orden
              </Button>
            </Space>
          </div>
        )}
        {disableForm && (
           <div style={{ position: 'sticky', bottom: 0, background: '#fff', padding: '16px', textAlign: 'right', boxShadow: '0 -2px 10px rgba(0,0,0,0.1)', zIndex: 10, margin: '24px -24px -24px -24px', borderRadius: '0 0 8px 8px' }}>
             <Button onClick={() => history.push('/orders')}>Volver</Button>
           </div>
        )}
      </Form>

      <Modal
        title="Sincronizar Inventario de Producción"
        open={isSyncModalVisible}
        onOk={handleSyncInventory}
        onCancel={() => setIsSyncModalVisible(false)}
        confirmLoading={syncing}
        okText="Reportar"
        cancelText="Cancelar"
      >
        <p>Selecciona las bodegas para realizar los movimientos de inventario. Se descontarán los insumos de la bodega de origen y se ingresarán los productos a la bodega de destino.</p>
        <div style={{ marginTop: 16 }}>
          <label style={{ display: 'block', marginBottom: 8 }}>Bodega Origen (Consumo de Insumos):</label>
          <Select 
            style={{ width: '100%' }} 
            placeholder="Seleccione bodega origen"
            value={storehouseFrom}
            onChange={setStorehouseFrom}
          >
            {storehouseData?.storehouse?.map((s: any) => (
              <Option key={s.id} value={s.id}>{s.name || s.alias}</Option>
            ))}
          </Select>
        </div>
        <div style={{ marginTop: 16 }}>
          <label style={{ display: 'block', marginBottom: 8 }}>Bodega Destino (Ingreso de Productos):</label>
          <Select 
            style={{ width: '100%' }} 
            placeholder="Seleccione bodega destino"
            value={storehouseTo}
            onChange={setStorehouseTo}
          >
            {storehouseData?.storehouse?.map((s: any) => (
              <Option key={s.id} value={s.id}>{s.name || s.alias}</Option>
            ))}
          </Select>
        </div>
      </Modal>
    </Card>
  );
};

export default OrderForm;
